import streamSaver from 'streamsaver';
import * as zip from '@zip.js/zip.js';
import { Modal } from 'antd';
import { DownloadingState, useStore } from '~src/store';
import { appLog } from '~src/utils/log';
import { buildFolderWithPathStructure } from '~src/utils/downloader/shared';
import {
  newSingleTenderFolderFileDownload,
  getTendersURLs,
  loadTendersRegistrationCoreFolders,
  loadTenderSubfolders,
} from '~src/services/api';
import type { DbTenderFolder, DbFile, DbAttachment } from '~src/types/db';

// ==========================================
// TYPES
// ==========================================
type MixedFile = DbFile & DbAttachment;

interface DownloadTenderFolderMultipleParams {
  folders: DbTenderFolder[];
  files: MixedFile[];
  allFolders: DbTenderFolder[];
  projectId: string | number;
  tenderId: string | number;
  currentFolderId?: string | number | null;
  rootFolderId?: string | number | null;
  fileVersionsContext: any;
  onDownloadStarted?: () => void;
}

// ==========================================
// ZIP STREAM CLASS
// ==========================================
class ZipStream {
  private zipWriter: zip.ZipWriter<WritableStream<any>> | null = null;
  private writableStream: WritableStream<any> | null = null;
  public successLog: string[] = [];
  public errorLog: string[] = [];
  public fileMap: Map<string, number> = new Map();
  public isAborted = false;

  private currentFileAlreadyStreamed = 0;
  private totalFiles = 0;
  private totalSize = 0;
  private startTime = 0;
  public endTime = 0;

  constructor(totalFiles: number, totalSize: number) {
    this.totalFiles = totalFiles;
    this.totalSize = totalSize;
    useStore.getState().changeTotalFileSize(totalSize);
  }

  start(zipName: string) {
    try {
      this.startTime = performance.now();
      this.writableStream = streamSaver.createWriteStream(zipName);
      this.zipWriter = new zip.ZipWriter(this.writableStream);
    } catch (error) {
      this.isAborted = true;
    }
  }

  private getUniqueFileName(filePath: string): string {
    let uniquePath = filePath;
    let count = 1;
    const match = filePath.match(/^(.*?)(\(\d+\))?(\.[^/.]+)?$/);
    const baseName = match?.[1] || filePath;
    const extension = match?.[3] || '';

    while (this.fileMap.has(uniquePath)) {
      uniquePath = `${baseName}(${count})${extension}`;
      count++;
    }

    this.fileMap.set(uniquePath, count);
    return uniquePath;
  }

  async addFile(fullPath: string, blob: Blob) {
    if (this.isAborted || !this.zipWriter) return;

    const uniquePath = this.getUniqueFileName(fullPath);
    try {
      useStore.getState().changeDownloadingState(DownloadingState.FETCHING);
      await this.zipWriter.add(uniquePath, new zip.BlobReader(blob));
      this.currentFileAlreadyStreamed++;
      useStore.getState().currentDownloadFile(this.currentFileAlreadyStreamed);
      this.successLog.push(
        `${uniquePath} - success (${(blob.size / 1024 / 1024).toFixed(2)} MB)`
      );
    } catch (error: any) {
      const msg = `${uniquePath} (0.00 MB) - ERROR: ${error?.message}`;
      this.errorLog.push(msg);
      useStore.getState().addError(msg);
      this.isAborted = true;
    }
  }

  async addEmptyFolder(folderPath: string) {
    if (this.isAborted || !this.zipWriter) return;
    try {
      const uniqueFolderPath = folderPath.endsWith('/')
        ? folderPath
        : folderPath + '/';

      if (this.fileMap.has(uniqueFolderPath)) return;

      await this.zipWriter.add(uniqueFolderPath, new zip.TextReader(''));
      this.fileMap.set(uniqueFolderPath, 1);
    } catch (error) {}
  }

  async finish() {
    try {
      if (!this.zipWriter || this.isAborted) return;
      if (this.endTime !== 0) return;

      this.endTime = performance.now();
      const totalSizeMB = (this.totalSize / 1024 / 1024).toFixed(2);
      const timeTaken = (this.endTime - this.startTime) / 1000;
      const speed = (this.totalSize / 1024 / 1024 / timeTaken).toFixed(2);
      const totalErrors = this.errorLog.length;

      const mergedLogs: string[] = [];
      const successEntries = [...this.successLog];
      const errorEntries = [...this.errorLog];
      while (successEntries.length || errorEntries.length) {
        if (successEntries.length) mergedLogs.push(successEntries.shift()!);
        if (errorEntries.length) mergedLogs.push(errorEntries.shift()!);
      }

      const finalLog = [
        ...mergedLogs,
        `\n==== Summary V2 ====`,
        `Total files - ${this.totalFiles}`,
        `Total errors - ${totalErrors}`,
        `Total size - ${totalSizeMB} MB`,
        `Download speed - ${speed} MB/s`,
        `Time taken - ${timeTaken.toFixed(2)} s`,
      ];
      if (totalErrors > 0) {
        finalLog.push(`\n==== ${totalErrors} Errors ====`, ...this.errorLog);
      }

      const logBlob = new Blob([finalLog.join('\n')], { type: 'text/plain' });
      if (!this.fileMap.has('download_log.txt')) {
        await this.zipWriter.add(
          'download_log.txt',
          new zip.BlobReader(logBlob)
        );
      }

      await this.zipWriter.close();
      useStore.getState().changeDownloadingState(DownloadingState.FINISHING);
      setTimeout(
        () => useStore.getState().changeDownloadingActive(false),
        1500
      );
    } catch (error) {
      this.isAborted = true;
    }
  }
}

// ==========================================
// WORKERS CONFIGURATION
// ==========================================
const createWorker = () => {
  return new Worker(
    URL.createObjectURL(
      new Blob(
        [
          `self.onmessage = async (event) => {
            const { file } = event.data;
            try {
              const response = await fetch(file.url);
              if (!response.ok) {
                throw new Error(\`HTTP \${response.status} - \${response.statusText}\`);
              }
              const blob = await response.blob();
              self.postMessage({ success: true, file, blob });
            } catch (error) {
              self.postMessage({ success: false, file, error: error.message });
            }
          };`,
        ],
        { type: 'application/javascript' }
      )
    )
  );
};

// ==========================================
// HELPERS
// ==========================================
const uniqFoldersById = (folders: DbTenderFolder[]) => {
  const map = new Map<string, DbTenderFolder>();
  for (const f of folders) map.set(String(f.id), f);
  return Array.from(map.values());
};

const ModalConfirmDownload = async () => {
  return new Promise<boolean>((resolve) => {
    Modal.confirm({
      title: 'Large download',
      content:
        'Selected content is large (>500 files). Downloading may take time. Continue?',
      okText: 'Download',
      cancelText: 'Cancel',
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });
};

const loadAllTenderSubfoldersRecursively = async ({
  projectId,
  tenderId,
  startFolderIds,
}: {
  projectId: string | number;
  tenderId: string | number;
  startFolderIds: number[];
}): Promise<DbTenderFolder[]> => {
  const visited = new Set<number>();
  const result: DbTenderFolder[] = [];
  const queue: number[] = [...startFolderIds];

  while (queue.length) {
    const currentId = queue.shift()!;
    if (visited.has(currentId)) continue;
    visited.add(currentId);

    let children: DbTenderFolder[] = [];
    try {
      children = await loadTenderSubfolders(null, {
        projectId,
        tenderId,
        rootFolderId: currentId,
      });
    } catch (e) {
      children = [];
    }

    if (children?.length) {
      result.push(...children);
      for (const ch of children) {
        if (ch?.id != null) queue.push(Number(ch.id));
      }
    }
  }
  return uniqFoldersById(result);
};

// ХЕЛПЕР ДЛЯ ПОШУКУ СПРАВЖНЬОГО ROOT FOLDER ID У ДЕРЕВІ
const getActualRootFolderId = (
  targetFolderId: string | number | null | undefined,
  tree: any[]
): string | number => {
  if (!targetFolderId) return '';
  let current = tree.find((f) => String(f.id) === String(targetFolderId));
  while (current && current.parent_id) {
    const parent = tree.find((f) => String(f.id) === String(current.parent_id));
    if (!parent) break;
    current = parent;
  }
  return current?.id || targetFolderId;
};

// ==========================================
// MAIN DOWNLOAD FUNCTION
// ==========================================
export const downloadTenderFolderMultipleItems = async ({
  folders,
  files,
  allFolders,
  projectId,
  tenderId,
  currentFolderId,
  rootFolderId,
  fileVersionsContext,
  onDownloadStarted,
}: DownloadTenderFolderMultipleParams): Promise<void> => {
  try {
    const fileQueue: any[] = [];
    let totalFiles = 0;
    let totalSize = 0;
    const folderPathsToCreate: string[] = [];

    // -------------------------------------------------------------
    // PREPARE: Global Context Tree (with Deep Folder Fetching)
    // -------------------------------------------------------------
    let tenderRootFolders: any[] = [];
    try {
      tenderRootFolders = await loadTendersRegistrationCoreFolders(null, {
        projectId: String(projectId),
        tenderId: String(tenderId),
      });
    } catch (e) {}

    const allDescendants = await loadAllTenderSubfoldersRecursively({
      projectId,
      tenderId,
      startFolderIds: tenderRootFolders.map((f) => Number(f.id)),
    });

    const fullContextTree = uniqFoldersById([
      ...(allFolders || []),
      ...(tenderRootFolders || []),
      ...(allDescendants || []),
      ...folders,
    ]);

    // ==========================================
    // 1. PROCESS SELECTED FOLDERS
    // ==========================================
    for (const folder of folders) {
      const rootFolderName = folder.name;
      const structure = buildFolderWithPathStructure(
        folder as any,
        fullContextTree as any
      );

      const rootFolderEntry = structure.find(
        (s: any) => String(s.id) === String(folder.id)
      );

      let parentPathPrefix = '';
      if (rootFolderEntry && rootFolderEntry.path) {
        const parts = rootFolderEntry.path.split('/');
        if (parts.length > 1) {
          parts.pop();
          parentPathPrefix = parts.join('/') + '/';
        }
      }

      const currentFolderPaths = uniqFoldersById(
        structure.map((s: any) => ({ id: s.id, name: s.path } as any))
      )
        .map((x: any) => x.name)
        .filter(Boolean)
        .map((p: string) => {
          if (parentPathPrefix && p.startsWith(parentPathPrefix)) {
            return p.substring(parentPathPrefix.length);
          }
          return p;
        });

      const adjustedRootName = rootFolderName;
      const allPathsSet = new Set<string>(currentFolderPaths);
      if (!allPathsSet.has(adjustedRootName)) {
        currentFolderPaths.unshift(adjustedRootName);
      }
      folderPathsToCreate.push(...currentFolderPaths);

      // STATIC TENDER ROOT FOLDER - REQUIRES API
      const resolvedRootFolderId =
        rootFolderId || getActualRootFolderId(folder.id, fullContextTree);

      const folderFiles = await newSingleTenderFolderFileDownload('files', {
        projectId,
        tenderId,
        rootFolderId: resolvedRootFolderId,
        folderId: folder.id,
      } as any);

      if (folderFiles && folderFiles.length > 0) {
        totalFiles += folderFiles.length;
        totalSize += folderFiles.reduce(
          (acc: number, f: any) => acc + (f.file_size || 0),
          0
        );

        folderFiles.forEach((file: any) => {
          const fileFolderId = file.folder_id;

          const rawFolderPath =
            structure.find((f: any) => {
              if (fileFolderId == null) return false;
              return (
                String(f.id) === String(fileFolderId) ||
                (f.using_key != null &&
                  String(f.using_key) === String(fileFolderId))
              );
            })?.path || '';

          let safeFolderPath = rawFolderPath;
          if (parentPathPrefix && rawFolderPath.startsWith(parentPathPrefix)) {
            safeFolderPath = rawFolderPath.substring(parentPathPrefix.length);
          }

          if (!safeFolderPath) safeFolderPath = rootFolderName;

          fileQueue.push({
            using_key: file.using_key,
            fullPath: safeFolderPath
              ? `${safeFolderPath}/${file.file_name}`
              : file.file_name,
          });
        });
      }
    }

    // ==========================================
    // 2. PROCESS SELECTED INDIVIDUAL FILES
    // ==========================================
    for (const file of files) {
      const fileName =
        file.name || file.title || (file as any).file_name || `file-${file.id}`;

      let calculatedPath = fileName;
      const fileFolderId = file.folder_id;

      if (fileFolderId) {
        const parentFolder = fullContextTree.find(
          (f) => String(f.id) === String(fileFolderId)
        );

        if (parentFolder) {
          const struct = buildFolderWithPathStructure(
            parentFolder as any,
            fullContextTree as any
          );
          const folderEntry = struct.find(
            (s) => String(s.id) === String(fileFolderId)
          );

          if (folderEntry && folderEntry.path) {
            let fullPath = folderEntry.path;

            for (const root of tenderRootFolders) {
              const rootPrefix = root.name + '/';
              if (fullPath.startsWith(rootPrefix)) {
                fullPath = fullPath.substring(rootPrefix.length);
                break;
              } else if (fullPath === root.name) {
                fullPath = '';
                break;
              }
            }

            calculatedPath = fullPath ? `${fullPath}/${fileName}` : fileName;
            if (fullPath) folderPathsToCreate.push(fullPath);
          }
        }
      }

      const versionId = fileVersionsContext[file.id]?.id;
      const stableVersions = (file.files || [])
        .map((f: any) => ({
          ...f,
          revision: f.serial_number ?? f.version ?? f.revision,
          version: f.serial_number ?? f.version ?? f.revision,
          id: f.registration_instance_id || f.file_id || f.id,
        }))
        .sort((a: any, b: any) => (b.revision || 0) - (a.revision || 0));

      const selectedVersion =
        stableVersions.find((v: any) => String(v.id) === String(versionId)) ||
        stableVersions[0] ||
        file.versions?.find((v: any) => String(v.id) === String(versionId)) ||
        file.versions?.[0];

      if (!selectedVersion) continue;

      const regId =
        selectedVersion.registration_id ||
        selectedVersion.versioned_file_id ||
        file.id;
      const serial = selectedVersion.version || selectedVersion.serial_number;

      // ТУТ ЗАЛИШИЛИ rootFolderId АБО file.folder_id (БЕЗ ПІДМІНИ)
      const downloadList = await newSingleTenderFolderFileDownload('files', {
        projectId,
        tenderId,
        rootFolderId: rootFolderId || file.folder_id,
        folderId: currentFolderId || file.folder_id,
        registration_ids: String(regId),
        serial,
      } as any);

      if (downloadList && downloadList.length > 0) {
        totalFiles += 1;
        totalSize += downloadList[0].file_size || 0;

        fileQueue.push({
          using_key: downloadList[0].using_key,
          fullPath: calculatedPath,
        });
      }
    }

    // ==========================================
    // 3. START ZIP STREAM
    // ==========================================
    if (fileQueue.length === 0 && folderPathsToCreate.length === 0) {
      throw new Error('No files or folders to download');
    }

    if (totalFiles > 500) {
      const confirm = await ModalConfirmDownload();
      if (!confirm) return;
    }

    if (onDownloadStarted) onDownloadStarted();

    const zipStream = new ZipStream(totalFiles, totalSize);
    useStore.getState().totalDownloadFiles(totalFiles);
    zipStream.start(`C-WEB-Project-${Date.now()}.zip`);

    if (zipStream.isAborted) return;

    const uniquePaths = [...new Set(folderPathsToCreate)];
    for (const p of uniquePaths) {
      await zipStream.addEmptyFolder(p);
    }

    if (fileQueue.length === 0) {
      await zipStream.finish();
      return;
    }

    // ==========================================
    // 4. PROCESS QUEUE WITH WORKERS
    // ==========================================
    const NUM_WORKERS = 5;
    const workers: Worker[] = [];
    let activeWorkers = 0;

    for (let i = 0; i < NUM_WORKERS; i++) {
      workers.push(createWorker());
    }

    const finishIfDone = async () => {
      if (fileQueue.length === 0 && activeWorkers === 0) {
        setTimeout(async () => {
          if (fileQueue.length === 0 && activeWorkers === 0) {
            await zipStream.finish();
            workers.forEach((w) => w.terminate());
          }
        }, 300);
      }
    };

    const assignFileToWorker = async (worker: Worker) => {
      if (zipStream.isAborted) return;

      const nextItem = fileQueue.shift();
      if (!nextItem) {
        await finishIfDone();
        return;
      }
      activeWorkers++;

      try {
        const urls = await getTendersURLs(Number(projectId), Number(tenderId), [
          nextItem.using_key,
        ]);

        const url = urls?.[0]?.url;

        if (!url) {
          throw new Error(`Missing URL for ${nextItem.fullPath}`);
        }

        worker.postMessage({
          file: { url, file_name: nextItem.fullPath },
        });
      } catch (err: any) {
        const errMsg = `${nextItem.fullPath} - ERROR: ${err.message}`;
        zipStream.errorLog.push(errMsg);
        useStore.getState().addError(errMsg);
        activeWorkers--;
        if (fileQueue.length > 0) {
          await assignFileToWorker(worker);
        } else {
          await finishIfDone();
        }
      }
    };

    for (const worker of workers) {
      worker.onmessage = async (event) => {
        const { success, file, blob, error } = event.data;
        try {
          if (success) {
            await zipStream.addFile(file.file_name, blob);
          } else {
            const msg = `${file.file_name} - ERROR: ${error}`;
            zipStream.errorLog.push(msg);
            useStore.getState().addError(msg);
          }
        } finally {
          activeWorkers--;
          if (fileQueue.length > 0) {
            await assignFileToWorker(worker);
          } else {
            await finishIfDone();
          }
        }
      };

      worker.onerror = async (e) => {
        const msg = `Worker Error: ${e.message}`;
        zipStream.errorLog.push(msg);
        useStore.getState().addError(msg);
        activeWorkers--;
        await finishIfDone();
      };

      await assignFileToWorker(worker);
    }
  } catch (err) {
    appLog('downloadTenderFolderMultipleItems -> global error', err);
    useStore.getState().changeDownloadingActive(false);
  }
};
